/* TLC — Finish preview for the custom skin studio.
   Applies the selected design's Skin Finish (Matte, Glossy, Cross Weave,
   Leather, Small Square) to the front + back pieces of the live preview.
   The renderer is the approved Finish tab engine from Tinker Lass Studio,
   using its default presets: the print stays as printed and the film only
   adds reflected light, texture glints and edge thickness on top.

   Applies to both the Live preview and the Print layout (cutfile) preview,
   and replaces the per-design Finish dropdown with finish tiles that show
   the customer's own artwork rendered in each finish.
   How it hooks in: zwapskin-custom-mockup.js draws each finished image with
   context.drawImage(image, 0, 0). This script watches for that draw, waits
   until the customer stops adjusting, then re-renders the skin areas (live:
   front/back masks; print: the pieces themselves) with the finish.
   The approved-layout PNG sent with the order is captured before the
   finish is applied, so production files are never affected.
   On phones, a floating mini-preview mirrors the live mockup while the
   customer is down at their design card.
   Ready-made Kindle / Kobo / Boox pages: finish swatches replace the Finish
   dropdown, and the finish is shown on the matching product photo. */
(function () {
'use strict';
const TAU = Math.PI * 2;

/* ---------- Finish engine: copied verbatim from Tinker Lass Studio's
   approved Finish tab (CORE-START..CORE-END), default presets ---------- */
/*CORE-START*/
const FX_TAU = Math.PI * 2, FX_RAD = Math.PI / 180;
function fxHash(x, y, s) {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function fxNoise(x, y, s) {
  const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
  const a = fxHash(ix, iy, s), b = fxHash(ix + 1, iy, s), c = fxHash(ix, iy + 1, s), d = fxHash(ix + 1, iy + 1, s);
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}
function fxFbm(x, y, s, o) {
  let v = 0, a = .5, t = 0;
  for (let i = 0; i < o; i++) { v += a * fxNoise(x, y, s + i * 17); t += a; x *= 2.03; y *= 2.03; a *= .5; }
  return v / t;
}
function fxWorley(x, y, s) {
  const ix = Math.floor(x), iy = Math.floor(y); let f1 = 9, f2 = 9;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const cx = ix + dx, cy = iy + dy, px = cx + fxHash(cx, cy, s), py = cy + fxHash(cx, cy, s + 4);
    const d = (px - x) * (px - x) + (py - y) * (py - y);
    if (d < f1) { f2 = f1; f1 = d; } else if (d < f2) f2 = d;
  }
  return Math.sqrt(f2) - Math.sqrt(f1);
}
const fxSmooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/* irregular threads running along a, spaced (on average) 1 unit apart in b */
function fxThreads(a, b, seed) {
  const bw = b + .9 * (fxFbm(b * .21, seed * 3.1, seed, 2) - .5) + .3 * (fxNoise(a * .03, b * .08, seed + 9) - .5);
  const idx = Math.floor(bw), d = bw - idx - .5;
  const dd = Math.abs(d - (fxHash(idx, seed, 61) - .5) * .5);
  const th = .03 + .05 * fxHash(idx, seed, 62);
  const prof = Math.exp(-(dd / th) * (dd / th));
  const dash = fxNoise(a * (.05 + .14 * fxHash(idx, seed, 63)) + idx * 13.1, idx * .7, seed + 5);
  return prof * fxSmooth(.32, .55, dash) * (.4 + .6 * fxHash(idx, seed, 64));
}
function fxLeather(u, v) {
  const wx = u + .35 * (fxFbm(u * .3, v * .3, 21, 2) - .5), wy = v + .35 * (fxFbm(u * .3 + 7, v * .3, 22, 2) - .5);
  const c1 = 1 - fxSmooth(0, .09, fxWorley(wx, wy, 3));
  const c2 = 1 - fxSmooth(0, .07, fxWorley(wx * 2.2 + 3.1, wy * 2.2 + 1.7, 11));
  return .9 - Math.max(c1, c2 * .55) + .14 * fxFbm(u * 1.5, v * 1.5, 23, 2);
}
function fxOil(u, v) {
  const th = fxFbm(u * .05, v * .05, 71, 2) * FX_TAU * .7, c = Math.cos(th), s = Math.sin(th);
  return fxFbm((u * c + v * s) * .5, (-u * s + v * c) * 1.4, 73, 3) * .6 + .4 * fxFbm(u * 1.2, v * 1.2, 75, 2);
}
const fxHmCache = new Map();
function fxHeightMap(type, W, H, ppm, size, angle) {
  const key = type + '|' + W + '|' + H + '|' + ppm.toFixed(5) + '|' + size + '|' + angle;
  let h = fxHmCache.get(key); if (h) return h;
  h = new Float32Array(W * H);
  const ca = Math.cos(angle * FX_RAD), sa = Math.sin(angle * FX_RAD), inv = 1 / (ppm * size);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const u = (x * ca + y * sa) * inv, v = (-x * sa + y * ca) * inv; let val;
    switch (type) {
      case 'peel': val = fxFbm(u, v, 51, 2); break;
      case 'frost': val = fxFbm(u, v, 1, 3); break;
      case 'weave': val = Math.max(fxThreads(u, v, 1), fxThreads(v, u, 2)); break;
      case 'leather': val = fxLeather(u, v); break;
      case 'oil': val = fxOil(u, v); break;
      case 'squares': { const fu = u - Math.floor(u) - .5, fv = v - Math.floor(v) - .5; val = 1 - 2 * Math.max(Math.abs(fu), Math.abs(fv)); break; }
      default: val = .5;
    }
    h[y * W + x] = val;
  }
  if (fxHmCache.size > 14) fxHmCache.delete(fxHmCache.keys().next().value);
  fxHmCache.set(key, h);
  return h;
}

function fxSparkle(src, p, L, ppm) {
  if (!(p.density > 0 && p.spBright > 0)) return null;
  const { W, H, data: d } = src;
  const n = Math.min(400000, Math.round((W / ppm) * (H / ppm) / 100 * p.density));
  if (!n) return null;
  const buf = new Float32Array(W * H), rp = p.spSize * ppm * .5, cover = Math.min(1, Math.max(.75, Math.PI * rp * rp));
  const a = L.angle * FX_RAD, cA = Math.cos(a), sA = Math.sin(a), lx = L.px * W, ly = L.py * H, diag = Math.hypot(W, H);
  const add = (x, y, v) => { if (x >= 0 && y >= 0 && x < W && y < H) buf[y * W + x] += v; };
  for (let i = 0; i < n; i++) {
    const fx = fxHash(i, 1, 77) * W, fy = fxHash(i, 2, 77) * H, xi = fx | 0, yi = fy | 0;
    if (d[(yi * W + xi) * 4 + 3] < 8) continue;
    let br = fxHash(i, 3, 77); br = .35 + .65 * br * br;
    const tt = ((fx - lx) * cA + (fy - ly) * sA) / diag;
    let gl = .5 + .5 * Math.cos(fxHash(i, 4, 77) * FX_TAU + tt * 14); gl = gl * gl;
    const v = p.spBright * br * (.75 + .25 * gl);
    if (rp < .8) add(xi, yi, v * cover);
    else { const R = Math.ceil(rp); for (let dy = -R; dy <= R; dy++) for (let dx = -R; dx <= R; dx++) { const q = (dx * dx + dy * dy) / (rp * rp); if (q < 1) add(xi + dx, yi + dy, v * (1 - q)); } }
    if (p.twinkle > 0 && fxHash(i, 5, 77) * 100 < p.twinkle) {
      const len = Math.max(1, Math.round(rp * 7));
      for (let k = 1; k <= len; k++) { const f = v * .85 * Math.pow(1 - k / (len + 1), 2); add(xi + k, yi, f); add(xi - k, yi, f); add(xi, yi + k, f); add(xi, yi - k, f); }
      add(xi, yi, v);
    }
  }
  return buf;
}

const fxEdgeCache = new Map();
function fxEdgeAlpha(src, rad) {
  const key = src.W + 'x' + src.H + ':' + rad; let e = fxEdgeCache.get(key); if (e && e.d === src.data) return e.a;
  const { W, H, data: d } = src; let a = new Float32Array(W * H), t = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) a[i] = d[i * 4 + 3] / 255;
  const pass = (inp, out, horiz) => { const n = horiz ? W : H, m = horiz ? H : W, win = rad * 2 + 1;
    for (let j = 0; j < m; j++) { let sum = 0; const at = k => { k = k < 0 ? 0 : k >= n ? n - 1 : k; return horiz ? inp[j * W + k] : inp[k * W + j]; };
      for (let k = -rad; k <= rad; k++) sum += at(k);
      for (let k = 0; k < n; k++) { if (horiz) out[j * W + k] = sum / win; else out[k * W + j] = sum / win; sum += at(k + rad + 1) - at(k - rad); } } };
  pass(a, t, true); pass(t, a, false); pass(a, t, true); pass(t, a, false);
  if (fxEdgeCache.size > 6) fxEdgeCache.delete(fxEdgeCache.keys().next().value);
  fxEdgeCache.set(key, { d: src.data, a }); return a;
}
const FX_SILVER = [.14, .02, .14, .02];
/* The print stays as printed; the film only adds reflected light on top. */
function fxRender(src, p, L, ppm) {
  const { W, H, data: d } = src;
  const img = new ImageData(W, H), o = img.data;
  const hm = (p.grain !== 'none' && (p.relief > 0 || p.crease > 0)) ? fxHeightMap(p.grain, W, H, ppm, p.size, p.angle) : null;
  const aa = Math.min(1, (p.size * ppm) / 2.5);
  const k = p.relief * p.size * .25 * ppm * aa;
  const la = L.angle * FX_RAD, el = L.elev * FX_RAD;
  const Lx = Math.cos(la) * Math.cos(el), Ly = Math.sin(la) * Math.cos(el), Lz = Math.sin(el);
  let Hx = Lx, Hy = Ly, Hz = Lz + 1; const hn = Math.hypot(Hx, Hy, Hz); Hx /= hn; Hy /= hn; Hz /= hn;
  const SH = 24, flat = Math.pow(Hz, SH);
  const lx = L.px * W, ly = L.py * H, diag = Math.hypot(W, H), ca = Math.cos(la), sa = Math.sin(la);
  const bw = Math.max(.002, p.boxW * .5), bl = Math.max(.002, p.boxL * .5);
  const sw = Math.max(.003, p.soft * bw * 1.6), sl = Math.max(.003, p.soft * bl * .9);
  const DISP = .03, CURVE = .25;
  const sp = fxSparkle(src, p, L, ppm);
  const erad = Math.max(1, Math.round(.45 * ppm)), ea = p.bevel ? fxEdgeAlpha(src, erad) : null;
  const eWide = p.envDark ? fxEdgeAlpha(src, Math.max(2, Math.round(2.5 * ppm))) : null;
  const holo = p.holo > 0 || p.silver > 0, hc = Math.max(.3, p.size), ha = p.angle * FX_RAD, ca2 = Math.cos(ha), sa2 = Math.sin(ha);
  const { crease, sat, contrast: con, haze, reflect, glow, texMix, glint } = p;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const j = y * W + x, i = j * 4, A = d[i + 3];
      if (!A) continue;
      let r = d[i], g = d[i + 1], b = d[i + 2];
      if (sat !== 1 || con !== 1) {
        const lum = .299 * r + .587 * g + .114 * b;
        r = (lum + (r - lum) * sat - 128) * con + 128; g = (lum + (g - lum) * sat - 128) * con + 128; b = (lum + (b - lum) * sat - 128) * con + 128;
      }
      if (haze) { r += (228 - r) * haze; g += (228 - g) * haze; b += (228 - b) * haze; }
      let nx = 0, ny = 0, nz = 1, micro = 1;
      if (hm) {
        const hl = hm[x > 0 ? j - 1 : j], hr = hm[x < W - 1 ? j + 1 : j], hu = hm[y > 0 ? j - W : j], hd = hm[y < H - 1 ? j + W : j];
        nx = -(hr - hl) * k; ny = -(hd - hu) * k;
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1); nx *= inv; ny *= inv; nz = inv;
        if (crease) { const f = 1 - crease * Math.max(0, .9 - hm[j]) * 1.2; r *= f; g *= f; b *= f; }
        micro = Math.min(2.2, Math.pow(Math.max(0, nx * Hx + ny * Hy + nz * Hz), SH) / flat);
        if (p.surface) {
          const sh = (nx * Lx + ny * Ly + nz * Lz - Lz) / Math.max(.15, 1 - Lz) * p.surface * 1.6;
          if (sh > 0) { const q = Math.min(1, sh) * .7; r += q * (255 - r); g += q * (255 - g); b += q * (255 - b); }
        }
      }
      const dx = x - lx, dy = y - ly;
      const t = (dx * ca + dy * sa) / diag, s = (-dx * sa + dy * ca) / diag;
      const tt = t + CURVE * s * s + DISP * (nx * ca + ny * sa), ss = s + DISP * (-nx * sa + ny * ca);
      const at = Math.abs(tt), as = Math.abs(ss);
      let box = 0;
      if (at < bw && as < bl) {
        box = (at <= bw - sw ? 1 : fxSmooth(bw, bw - sw, at)) * (as <= bl - sl ? 1 : fxSmooth(bl, bl - sl, as));
        box *= .82 + .18 * (1 - as / bl);
      }
      if (p.twin) {
        const t2 = Math.abs(tt - bw * 2.6), bw2 = bw * .55, sw2 = sw * .55;
        if (t2 < bw2 && as < bl) box += p.twin * (t2 <= bw2 - sw2 ? 1 : fxSmooth(bw2, bw2 - sw2, t2)) * (as <= bl - sl ? 1 : fxSmooth(bl, bl - sl, as));
      }
      const broad = Math.exp(-(tt * tt + ss * ss) * 5);
      const texed = 1 + (micro - 1) * texMix;
      let R = (reflect * Math.min(1, box) + glow * broad) * texed;
      if (glint && micro > 1) R += glint * (micro - 1) * .35 * (.6 + .4 * broad);
      if (holo) {
        const mx = x / ppm, my = y / ppm, u = (mx * ca2 + my * sa2) / hc, v = (-mx * sa2 + my * ca2) / hc;
        const cu = Math.floor(u), cv = Math.floor(v), fu = u - cu - .5, fv = v - cv - .5;
        const vert = Math.abs(fv) >= Math.abs(fu); // top/bottom triangles form the bright vertical bowtie
        if (p.silver) {
          const lift = p.silver * (vert ? .16 : -.05);
          if (lift > 0) { r += lift * (255 - r); g += lift * (255 - g); b += lift * (255 - b); } else { r += lift * r; g += lift * g; b += lift * b; }
        }
        if (p.holo) {
          const ccu = (cu + .5) * hc, ccv = (cv + .5) * hc;
          const ccx = (ccu * ca2 - ccv * sa2) * ppm, ccy = (ccu * sa2 + ccv * ca2) * ppm;
          const dir = ha + (vert ? Math.PI / 2 : 0) + .5;
          const ph = ((ccx - lx) * Math.cos(dir) + (ccy - ly) * Math.sin(dir)) / diag * 3.5 + fxHash(cu, cv, 44) * .5;
          const along = vert ? Math.abs(fv) * 2 : Math.abs(fu) * 2, across = vert ? Math.abs(fu) / (Math.abs(fv) + 1e-3) : Math.abs(fv) / (Math.abs(fu) + 1e-3);
          let fl = Math.pow(.5 + .5 * Math.cos(FX_TAU * ph), 3) * (vert ? 1 : .45) * (.35 + .65 * fxHash(cu * 2 + (vert ? 1 : 0), cv, 45)) * (1 - .2 * across) * p.holo;
          if (fl > .01) {
            let hh = (ph * .8 + along * .35 + .1 + fxHash(cu, cv, 46) * .7) % 1;   // per-cell colour variety, no single-colour bands if (hh < 0) hh += 1;
            const h6 = hh * 6;
            const cr = Math.min(1, Math.max(0, Math.abs(h6 - 3) - 1)), cg = Math.min(1, Math.max(0, 2 - Math.abs(h6 - 2))), cb = Math.min(1, Math.max(0, 2 - Math.abs(h6 - 4)));
            const k = Math.min(1, fl) * .8;
            r += k * (110 + 145 * cr - r); g += k * (120 + 135 * cg - g); b += k * (130 + 125 * cb - b);
          }
        }
      }
      if (p.env) {
        /* plastic-film reflection: big milky patches along the diagonal, mottled like cling film */
        const es = p.envScale, u = tt * es, v = ss * es;
        const warp = fxFbm(u * 1.5 + 5, v * .8, 97, 3);
        const big = fxSmooth(.47, .7, fxFbm(u * 1.3 + warp * .9, v * .45 + warp * .5, 93, 3));
        if (big > 0) {
          const det = fxFbm(u * 10 + warp * 3, v * 5.5 + warp * 2, 99, 4);
          R += p.env * .72 * big * (.25 + .75 * fxSmooth(.32, .74, det));
        }
        if (p.envDark) {
          /* dark parts of the room reflected in the film: grey smudges that show even on white */
          const dn = fxFbm(u * 1.5 + 17 + warp * .7, v * .6 + warp * .4, 101, 3);
          const ew = eWide ? Math.min(1, Math.max(0, (1 - eWide[j]) * 2.6)) : 0;
          const dk = Math.min(1, fxSmooth(.47, .62, dn) + ew * fxSmooth(.36, .56, dn)) * (1 - big * .8);
          if (dk > 0) {
            const d2 = fxFbm(u * 12 + warp * 3 + 40, v * 6 + warp * 2, 103, 3);
            const f = 1 - p.envDark * .55 * dk * (.3 + .7 * fxSmooth(.3, .75, d2)); r *= f; g *= f; b *= f;
          }
        }
      }
      if (ea) {
        const ab = ea[j];
        if (ab < .995) {
          const gx = ea[x < W - 1 ? j + 1 : j] - ea[x > 0 ? j - 1 : j], gy = ea[y < H - 1 ? j + W : j] - ea[y > 0 ? j - W : j], mg = Math.hypot(gx, gy);
          if (mg > 1e-4) {
            const dd = -(gx * Lx + gy * Ly) / mg / Math.max(.2, Math.hypot(Lx, Ly)), ef = Math.min(1, mg * erad * 1.3) * p.bevel;
            if (dd > 0) R += dd * ef * .75; else { const f = 1 + dd * ef * .3; r *= f; g *= f; b *= f; }
          }
        }
      }
      if (p.shade) { const f = 1 - p.shade * fxSmooth(0, .6, -tt); r *= f; g *= f; b *= f; }
      if (R > 0) { if (R > 1) R = 1; const q = 1 - R; r = 255 - (255 - r) * q; g = 255 - (255 - g) * q; b = 255 - (255 - b) * q; }
      if (sp) { const sv = sp[j]; if (sv > 0) { const q = 1 - (sv > 1 ? 1 : sv); r = 255 - (255 - r) * q; g = 255 - (255 - g) * q; b = 255 - (255 - b) * q; } }
      o[i] = r; o[i + 1] = g; o[i + 2] = b; o[i + 3] = A;
    }
  }
  return img;
}

const FX_BASE = { grain: 'none', size: .5, angle: 0, relief: 0, surface: 0, texMix: .5, glint: 0, crease: 0, contrast: 1, sat: 1, haze: 0, reflect: .3, boxW: .15, boxL: .6, soft: .5, glow: .05, twin: 0, env: 0, envDark: 0, envScale: 3, shade: 0, bevel: 0, density: 0, spSize: .1, spBright: .85, twinkle: 0, holo: 0, silver: 0 };
const FX_DEF = {
  matte:   { bevel: .3, grain: 'frost', size: .2, relief: .5, surface: .15, texMix: .4, reflect: .12, boxW: .4, boxL: 1, soft: 1, glow: .05, contrast: .95, sat: .94, haze: .05 },
  glossy:  { grain: 'none', size: 2, reflect: 0, boxW: .5, boxL: 2, soft: 1, glow: .06, env: .85, envDark: .75, envScale: 2.6, shade: 0, bevel: .7, contrast: 1.05, sat: 1.08 },
  cross:   { bevel: .3, grain: 'weave', size: .8, relief: .7, surface: .45, texMix: .5, glint: .3, reflect: .18, boxW: .3, boxL: 1, soft: .8, glow: .06, sat: 1.02 },
  leather: { bevel: .3, grain: 'leather', size: 1.6, relief: .7, surface: .38, texMix: .5, glint: .2, crease: 0, reflect: .15, boxW: .3, boxL: 1, soft: .85, glow: .05, contrast: .98, haze: .02 },
  square:  { bevel: .3, grain: 'squares', size: 4, relief: 0, surface: 0, texMix: 0, reflect: .25, boxW: .3, boxL: 1.2, soft: .8, glow: .06, holo: 1, silver: .5 },
  shimmer: { bevel: .3, grain: 'frost', size: .15, relief: .15, surface: .05, reflect: .15, boxW: .3, boxL: 1, soft: .8, glow: .05, density: 800, spSize: .16, spBright: 1, twinkle: 1.5 },
  oil:     { bevel: .3, grain: 'oil', size: 1.4, relief: .5, surface: .35, texMix: .5, glint: .15, reflect: .2, boxW: .25, boxL: 1, soft: .7, glow: .06, contrast: 1.04, sat: 1.1 }
};
/*CORE-END*/

const LABELS = {matte:'Matte',glossy:'Glossy',cross:'Cross Weave',leather:'Leather',square:'Small Square',shimmer:'Shimmer',oil:'Oil Painting'};
const LIGHT = {px:.32,py:.28,angle:225,elev:40};   // Studio's default light
const PIECE_WIDTH_MM = 125;   // Studio treats artwork as 125 mm wide; same texture scale here
const DELAY_MS = 220;         // wait after the last redraw before applying the finish
const SHINE = { PERIOD: 3500, SWEEP: 1100, ALPHA: .42, RES: 640 };   // Glossy moving shine (live preview)

function finishId(name) {
  const n = String(name || '').toLowerCase();
  if (/gloss/.test(n)) return 'glossy';
  if (/matte/.test(n)) return 'matte';
  if (/leather/.test(n)) return 'leather';
  if (/cross|weave/.test(n)) return 'cross';
  if (/square|holo/.test(n)) return 'square';
  if (/shimmer|flash/.test(n)) return 'shimmer';
  if (/oil/.test(n)) return 'oil';
  return null;
}

/* ---------- masks ---------- */
const imgCache = new Map();
function loadImage(url) {
  if (imgCache.has(url)) return imgCache.get(url);
  const p = new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = url;
  });
  imgCache.set(url, p);
  return p;
}

// Mask scaled to the preview canvas size, cropped to its own bounding box.
const maskCache = new Map();
async function pieceMask(url, W, H) {
  const key = url + '|' + W + 'x' + H;
  if (maskCache.has(key)) return maskCache.get(key);
  const im = await loadImage(url);
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const cx = c.getContext('2d');
  cx.drawImage(im, 0, 0, W, H);
  const d = cx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (d[(y * W + x) * 4 + 3] > 10) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) { maskCache.set(key, null); return null; }
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  const crop = document.createElement('canvas'); crop.width = w; crop.height = h;
  crop.getContext('2d').drawImage(c, x0, y0, w, h, 0, 0, w, h);
  const info = { x0, y0, w, h, crop, work: new Map() };
  maskCache.set(key, info);
  return info;
}
function workMaskAlpha(info, ww, hh) {
  const key = ww + 'x' + hh;
  if (info.work.has(key)) return info.work.get(key);
  const c = document.createElement('canvas'); c.width = ww; c.height = hh;
  const cx = c.getContext('2d');
  cx.imageSmoothingQuality = 'high';
  cx.drawImage(info.crop, 0, 0, ww, hh);
  const d = cx.getImageData(0, 0, ww, hh).data;
  const a = new Uint8Array(ww * hh);
  for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
  info.work.set(key, a);
  return a;
}

/* ---------- render off the main thread ----------
   The finish engine runs in a Web Worker built from the exact engine
   functions above, so the page never freezes while it renders. If a worker
   can't start, it falls back to the main thread. */
let worker = null, workerFailed = false, jobSeq = 0;
const jobs = new Map();
function getWorker() {
  if (worker || workerFailed) return worker;
  try {
    const fns = [fxHash, fxNoise, fxFbm, fxWorley, fxThreads, fxLeather, fxOil, fxHeightMap, fxSparkle, fxEdgeAlpha, fxRender].map((f) => f.toString()).join('\n');
    const src = 'const FX_TAU=' + FX_TAU + ',FX_RAD=' + FX_RAD + ';const FX_SILVER=' + JSON.stringify(FX_SILVER) + ';' +
      'const fxSmooth=' + fxSmooth.toString() + ';const fxHmCache=new Map(),fxEdgeCache=new Map();\n' + fns +
      '\nonmessage=function(e){var m=e.data;try{var data=new Uint8ClampedArray(m.buf);var out=fxRender({W:m.W,H:m.H,data:data},m.preset,m.light,m.ppm);' +
      'postMessage({id:m.id,buf:out.data.buffer},[out.data.buffer]);}catch(err){postMessage({id:m.id,error:String(err)});}};';
    worker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
    worker.onmessage = (e) => { const j = jobs.get(e.data.id); if (!j) return; jobs.delete(e.data.id); e.data.error ? j.reject(new Error(e.data.error)) : j.resolve(new Uint8ClampedArray(e.data.buf)); };
    worker.onerror = () => { workerFailed = true; worker = null; jobs.forEach((j) => j.reject(new Error('worker failed'))); jobs.clear(); };
  } catch (e) { workerFailed = true; worker = null; }
  return worker;
}
function renderPixels(W, H, data, preset, ppm) {
  const w = getWorker();
  if (!w) return Promise.resolve(fxRender({ W, H, data }, preset, LIGHT, ppm).data);
  return new Promise((resolve, reject) => {
    const id = ++jobSeq; jobs.set(id, { resolve, reject });
    w.postMessage({ id, W, H, buf: data.buffer, preset, light: LIGHT, ppm }, [data.buffer]);
  }).catch(() => null);
}

function workMax() { return window.matchMedia('(max-width: 749px)').matches ? 1000 : 1400; }

/* ---------- preview tuning ----------
   The Studio presets are real-size, so at normal view some texture is only
   a few screen pixels. Per finish, for the preview and tiles only:
   - Small Square: texture kept at least MIN_CELL_PX on screen and
     texture/glint strength raised (approved as it looks).
   - Leather: soft pebble shading, no glints, pebbles ~9 screen px.
   - Matte: no grain boost (it should read smooth, not textured).
   - Cross Weave: finer and softer threads, no boost (Studio strength read
     as white scratches at preview size).
   - Glossy: richer, more saturated colour and one big soft glare (no lines,
     no cloudy patches), so it reads as glossy next to Matte. On the live
     preview a soft band of light also sweeps across it every few seconds. */
const BOOST = { STRENGTH: 1.5, MIN_CELL_PX: 7, MAX_SIZE_X: 1.5 };   // grain never more than 1.5x real size (bigger read as white lines on phones)
const PREVIEW_TUNE = {
  matte:   { boost: false },
  // Cross Weave: threads kept at least 4 screen px apart (never wider than
  // 1.6 mm), deeper relief and a little shading in the gaps so it shows on
  // light designs too.
  cross:   { boost: false, minCellPx: 4, maxSize: 1.6, set: { size: .8, relief: .95, surface: .5, glint: .3, texMix: .6, crease: .12 } },
  glossy:  { boost: false, set: { env: 0, envDark: 0, twin: 0, reflect: .32, boxW: .75, boxL: 2.4, soft: 1, glow: .18, sat: 1.2, contrast: 1.12, bevel: .8 } },
  // Leather: real-size grain highlights read as white speckle at normal view,
  // so the preview uses soft pebble shading with no glints, and keeps the
  // pebbles about 9 screen px wide (never larger than 4 mm).
  leather: { boost: false, minCellPx: 9, maxSize: 4, set: { relief: .8, surface: .38, glint: 0, texMix: .25, crease: .08 } },
  square:  { boost: true }
};
function previewPreset(id, screenPxPerMm) {
  const p = Object.assign({}, FX_BASE, FX_DEF[id]);
  const tune = PREVIEW_TUNE[id] || { boost: false };
  if (tune.set) Object.assign(p, tune.set);
  if (tune.minCellPx && screenPxPerMm > 0) p.size = Math.min(tune.maxSize || p.size, Math.max(p.size, tune.minCellPx / screenPxPerMm));
  if (!tune.boost) return p;
  if (p.grain !== 'none' && p.grain !== 'squares' && BOOST.MIN_CELL_PX > 0 && screenPxPerMm > 0) {
    const need = BOOST.MIN_CELL_PX / screenPxPerMm;
    if (p.size < need) p.size = Math.min(p.size * BOOST.MAX_SIZE_X, need);
  }
  const k = BOOST.STRENGTH;
  p.surface = Math.min(1, p.surface * k);
  p.glint = Math.min(1, p.glint * k);
  p.texMix = Math.min(1, p.texMix * k);
  p.reflect = Math.min(1, p.reflect * (1 + (k - 1) * .6));
  p.env = Math.min(1, p.env * (1 + (k - 1) * .3));
  return p;
}

// Renders the finish for one region at screen resolution, then applies only
// the change it makes (lighter / darker per pixel) onto the full-resolution
// image, so the design itself stays perfectly sharp at every zoom level.
// reg.mask = cropped mask (live mockup) or null (print layout: the pieces'
// own transparency is the mask).
async function renderRegion(snapshot, reg, preset, ppmFull) {
  const { x0, y0, w, h, mask } = reg;
  const s = Math.min(1, workMax() / Math.max(w, h));
  const ww = Math.max(1, Math.round(w * s)), hh = Math.max(1, Math.round(h * s));

  const lo = document.createElement('canvas'); lo.width = ww; lo.height = hh;
  const lx = lo.getContext('2d');
  lx.imageSmoothingQuality = 'high';
  lx.drawImage(snapshot, x0, y0, w, h, 0, 0, ww, hh);
  const baseLo = lx.getImageData(0, 0, ww, hh).data;
  if (mask) {
    const alphaLo = workMaskAlpha(mask, ww, hh);
    for (let i = 0; i < alphaLo.length; i++) baseLo[i * 4 + 3] = alphaLo[i] > 8 ? 255 : 0;
  } else {
    for (let i = 3; i < baseLo.length; i += 4) baseLo[i] = baseLo[i] > 8 ? 255 : 0;
  }
  const keep = new Uint8ClampedArray(baseLo);

  let fin = await renderPixels(ww, hh, new Uint8ClampedArray(baseLo), preset, ppmFull * s);
  if (!fin || fin.length !== ww * hh * 4) fin = fxRender({ W: ww, H: hh, data: keep }, preset, LIGHT, ppmFull * s).data;

  const pos = new ImageData(ww, hh), neg = new ImageData(ww, hh);
  for (let i = 0; i < ww * hh; i++) {
    const k = i * 4;
    if (!keep[k + 3]) continue;
    for (let c = 0; c < 3; c++) {
      const dlt = fin[k + c] - keep[k + c];
      if (dlt > 0) pos.data[k + c] = dlt; else neg.data[k + c] = -dlt;
    }
    pos.data[k + 3] = 255; neg.data[k + 3] = 255;
  }
  const up = (img) => {
    const t = document.createElement('canvas'); t.width = ww; t.height = hh; t.getContext('2d').putImageData(img, 0, 0);
    const u = document.createElement('canvas'); u.width = w; u.height = h;
    const ux = u.getContext('2d'); ux.imageSmoothingQuality = 'high'; ux.drawImage(t, 0, 0, w, h);
    return ux.getImageData(0, 0, w, h).data;
  };
  const P = up(pos), N = up(neg);

  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const cx = c.getContext('2d');
  cx.drawImage(snapshot, x0, y0, w, h, 0, 0, w, h);
  const full = cx.getImageData(0, 0, w, h);
  const fd = full.data;
  for (let i = 0; i < fd.length; i += 4) {
    fd[i] = fd[i] + P[i] - N[i];
    fd[i + 1] = fd[i + 1] + P[i + 1] - N[i + 1];
    fd[i + 2] = fd[i + 2] + P[i + 2] - N[i + 2];
    fd[i + 3] = 255;
  }
  cx.putImageData(full, 0, 0);
  cx.globalCompositeOperation = 'destination-in';
  if (mask) cx.drawImage(mask.crop, 0, 0);
  else cx.drawImage(snapshot, x0, y0, w, h, 0, 0, w, h);
  return c;
}

/* ---------- Glossy shine (shared) ----------
   Every SHINE.PERIOD ms a soft band of light sweeps across the skin areas.
   Each frame clears the canvas, redraws the finished image, then the band,
   so semi-transparent parts (drop shadows) never build up. Stops when
   alive() turns false. Not used when the visitor prefers reduced motion. */
const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const shineMaskCache = new Map();
function shineMaskFor(regions, W, H) {
  const key = W + 'x' + H + regions.map((r) => r.x0 + ',' + r.y0 + ',' + r.w).join('|');
  if (shineMaskCache.has(key)) return shineMaskCache.get(key);
  const S = SHINE.RES, m = document.createElement('canvas'); m.width = S; m.height = Math.round(S * H / W);
  const mx = m.getContext('2d');
  regions.forEach((r) => { if (r.mask) mx.drawImage(r.mask.crop, r.x0 * S / W, r.y0 * S / W, r.w * S / W, r.h * S / W); });
  shineMaskCache.set(key, m);
  return m;
}
function runShine(ctx, draw, comp, mask, W, H, alive) {
  if (reduceMotion || !window.requestAnimationFrame || !mask) return null;
  const band = document.createElement('canvas'); band.width = mask.width; band.height = mask.height;
  const bx = band.getContext('2d');
  const t0 = performance.now() + 600;
  let wasSweeping = false, raf = 0, stopped = false;
  const tick = (now) => {
    if (stopped || !alive()) { raf = 0; return; }
    const t = (now - t0) % SHINE.PERIOD;
    const sweeping = now >= t0 && t < SHINE.SWEEP;
    if (sweeping || wasSweeping) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      draw(comp, 0, 0);
      if (sweeping) {
        const ph = t / SHINE.SWEEP, bw = band.width, bh = band.height, c = -0.25 + 1.5 * ph;
        bx.globalCompositeOperation = 'source-over';
        bx.clearRect(0, 0, bw, bh);
        const g = bx.createLinearGradient(0, 0, bw, bh);
        const st = (p, a) => { if (p > 0 && p < 1) g.addColorStop(p, 'rgba(255,255,255,' + a + ')'); };
        g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(255,255,255,0)');
        st(c - .16, 0); st(c - .05, SHINE.ALPHA * .55); st(c, SHINE.ALPHA); st(c + .05, SHINE.ALPHA * .55); st(c + .16, 0);
        bx.fillStyle = g; bx.fillRect(0, 0, bw, bh);
        bx.globalCompositeOperation = 'destination-in';
        bx.drawImage(mask, 0, 0);
        draw(band, 0, 0, W, H);
      }
      ctx.restore();
    }
    wasSweeping = sweeping;
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => { stopped = true; if (raf) cancelAnimationFrame(raf); raf = 0; };
}

/* ---------- one finish target per canvas (live mockup, print layout) ---------- */
function makeTarget(studio, canvas, opts) {
  const ctx = canvas.getContext('2d');
  const drawImg = ctx.drawImage;
  const T = { base: null, timer: 0, gen: 0, failed: false };

  const host = canvas.closest('.zss-preview-col') || canvas.parentElement;
  let badge = null;
  if (host) {
    badge = document.createElement('div');
    badge.className = 'tlc-finish-badge';
    badge.hidden = true;
    badge.setAttribute('aria-live', 'polite');
    host.appendChild(badge);
  }
  function setBadge(text, busy) {
    if (!badge) return;
    badge.hidden = !text;
    badge.textContent = text || '';
    badge.classList.toggle('is-busy', !!busy);
  }
  const visible = () => !opts.col || !opts.col.hidden;

  /* Glossy shine on this canvas (live preview only); see runShine(). */
  let stopShineFn = null;
  function stopShine() { if (stopShineFn) stopShineFn(); stopShineFn = null; }
  function startShine(comp, regions, W, H) {
    stopShine();
    if (!opts.shine) return;
    stopShineFn = runShine(ctx, (img, ...args) => drawImg.call(ctx, img, ...args), comp, shineMaskFor(regions, W, H), W, H,
      () => T.lastOut === comp && !busyAdding(studio) && visible());
  }

  function schedule(delay) {
    stopShine();
    clearTimeout(T.timer);
    if (!visible()) return;   // hidden print layout renders when it's opened
    const id = finishId(activeFinishName(studio));
    if (id && T.base && studio.querySelector('.zss-design-card')) setBadge(LABELS[id] + ' finish · rendering…', true);
    T.timer = setTimeout(apply, delay == null ? DELAY_MS : delay);
  }

  async function apply() {
    const my = ++T.gen;
    if (T.failed || !T.base || busyAdding(studio) || !visible()) return;
    if (!studio.querySelector('.zss-design-card')) { setBadge(''); return; }
    const id = finishId(activeFinishName(studio));
    if (!id) { setBadge(''); return; }
    const W = canvas.width, H = canvas.height, snapshot = T.base;
    if (snapshot.width !== W || snapshot.height !== H) return;
    try {
      const regions = await opts.regions(W, H);
      if (my !== T.gen || T.base !== snapshot) return;
      const ppm = opts.ppm(W, H, regions);
      const screenPxPerMm = canvas.offsetWidth ? ppm * (canvas.offsetWidth * (window.devicePixelRatio || 1)) / W : ppm * .35;
      const preset = previewPreset(id, screenPxPerMm);
      const parts = await Promise.all(regions.map((r) => renderRegion(snapshot, r, preset, ppm)));
      if (my !== T.gen || T.base !== snapshot || canvas.width !== W || busyAdding(studio)) return;

      // anything drawn on top of the pieces after them (print cut lines) is kept on top
      let overlay = null;
      if (opts.keepOverlay) {
        const now = ctx.getImageData(0, 0, W, H);
        // compare against what is under the lines: the clean pieces, or the
        // finish this script last drew for this same image
        const ref = (T.lastBase === snapshot && T.lastOut) ? T.lastOut : snapshot;
        const bc = document.createElement('canvas'); bc.width = W; bc.height = H;
        const bx = bc.getContext('2d'); bx.drawImage(ref, 0, 0);
        const b = bx.getImageData(0, 0, W, H).data, n = now.data;
        let any = false;
        for (let i = 0; i < n.length; i += 4) {
          if (n[i] === b[i] && n[i + 1] === b[i + 1] && n[i + 2] === b[i + 2] && n[i + 3] === b[i + 3]) n[i + 3] = 0; else any = true;
        }
        if (any) { overlay = document.createElement('canvas'); overlay.width = W; overlay.height = H; overlay.getContext('2d').putImageData(now, 0, 0); }
      }

      const comp = document.createElement('canvas'); comp.width = W; comp.height = H;
      const cc = comp.getContext('2d');
      cc.drawImage(snapshot, 0, 0);
      regions.forEach((r, i) => cc.drawImage(parts[i], r.x0, r.y0));
      T.lastOut = comp; T.lastBase = snapshot;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      drawImg.call(ctx, comp, 0, 0);
      if (overlay) drawImg.call(ctx, overlay, 0, 0);
      ctx.restore();
      setBadge(LABELS[id] + ' finish · preview');
      if (id === 'glossy') startShine(comp, regions, W, H); else stopShine();
    } catch (err) {
      T.failed = true;
      setBadge('');
      console.warn('[TLC finish] ' + opts.name + ' preview disabled:', err);
    }
  }

  // Catch each finished image the studio draws: drawImage(image, 0, 0).
  ctx.drawImage = function (img) {
    const r = drawImg.apply(this, arguments);
    if (arguments.length === 3 && img && img.width === canvas.width && img.height === canvas.height) {
      T.base = img;
      schedule();
    }
    return r;
  };

  if (opts.col && window.MutationObserver) {
    new MutationObserver(() => { if (visible()) schedule(60); }).observe(opts.col, { attributes: true, attributeFilter: ['hidden'] });
  }
  return { schedule };
}

function activeFinishName(studio) {
  const card = studio.querySelector('.zss-design-card.is-active') || studio.querySelector('.zss-design-card');
  const sel = card && card.querySelector('[data-zss-design-finish]');
  return sel ? sel.value : '';
}
function busyAdding(studio) {
  const btn = studio.querySelector('[data-zss-add-to-cart]');
  return !!(btn && btn.classList.contains('is-loading'));
}

/* ---------- finish picker in each design card ----------
   Replaces the Finish dropdown with five small swatches beside the
   quantity. Each swatch is a close-up of the customer's own artwork rendered
   in that finish (same engine as the preview), plus a one-line description
   of the selected finish. The studio's
   real dropdown stays in the card (hidden) and is what the tiles set, so
   ordering, pricing and variants work exactly as before. */
const FINISH_INFO = {
  matte:   { desc: 'Soft, glare-free and smooth.', note: 'Soft, glare-free and smooth' },
  glossy:  { desc: 'Shiny and vivid. Scratches more easily.', note: 'Shiny and vivid, scratches more easily' },
  cross:   { desc: 'Fine fabric texture you can feel.', note: 'Fine fabric texture you can feel' },
  leather: { desc: 'Pebbled grain with a soft feel.', note: 'Pebbled grain with a soft feel' },
  square:  { desc: 'Holographic shine, smooth to touch.', note: 'Holographic shine, smooth to touch' },
  shimmer: { desc: 'Fine sparkle that catches the light.', note: 'Fine sparkle' },
  oil:     { desc: 'Brush-stroke texture like a painting.', note: 'Brush-stroke texture' }
};
const DISCLAIMER = 'Finish previews are a guide only. The real finish may look slightly different in person.';
const shortName = (name) => String(name || '').replace(/\s*\(.*\)\s*$/, '').trim();

const SW_PX = 160, SW_MM = 11;   // each tile shows ~11 mm of the design (texture as seen on a ~56px tile)
const swatchCache = new Map();    // artwork src -> Map(finishId -> Promise<url>)
function artworkImage(src) {
  return new Promise((resolve, reject) => { const im = new Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = src; });
}
function swatchFor(src, id) {
  let m = swatchCache.get(src);
  if (!m) {
    m = new Map(); swatchCache.set(src, m);
    if (swatchCache.size > 16) swatchCache.delete(swatchCache.keys().next().value);
  }
  if (m.has(id)) return m.get(id);
  const p = (async () => {
    const im = await artworkImage(src);
    const c = document.createElement('canvas'); c.width = c.height = SW_PX;
    const cx = c.getContext('2d');
    cx.fillStyle = '#FFFFFF'; cx.fillRect(0, 0, SW_PX, SW_PX);
    const w = im.naturalWidth || im.width, h = im.naturalHeight || im.height;
    const side = Math.max(8, Math.min(w, h) * (src.indexOf('#closeup') > -1 ? 1 : 0.3));   // close-up from the centre of the design
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(im, (w - side) / 2, (h - side) / 2, side, side, 0, 0, SW_PX, SW_PX);
    if (!id) return c.toDataURL('image/jpeg', 0.85);
    const data = cx.getImageData(0, 0, SW_PX, SW_PX).data;
    const keep = new Uint8ClampedArray(data);
    const ppm = SW_PX / SW_MM;
    const preset = previewPreset(id, ppm);
    let out = await renderPixels(SW_PX, SW_PX, data, preset, ppm);
    if (!out || out.length !== SW_PX * SW_PX * 4) out = fxRender({ W: SW_PX, H: SW_PX, data: keep }, preset, LIGHT, ppm).data;
    cx.putImageData(new ImageData(out, SW_PX, SW_PX), 0, 0);
    return c.toDataURL('image/jpeg', 0.88);
  })();
  p.catch(() => m.delete(id));
  m.set(id, p);
  return p;
}
function paintSwatch(el, src, id) {
  if (!src) return;
  swatchFor(src, '').then((u) => { if (!el.dataset.done) el.style.backgroundImage = 'url(' + u + ')'; }).catch(() => {});
  swatchFor(src, id).then((u) => { el.dataset.done = '1'; el.style.backgroundImage = 'url(' + u + ')'; }).catch(() => {});
}

function setupPicker(studio) {
  const tray = studio.querySelector('[data-zss-design-tray]');
  const review = studio.querySelector('[data-zss-review-list]');
  if (!tray || tray.dataset.tlcPicker === '1') return;
  tray.dataset.tlcPicker = '1';

  // The theme's studio stylesheet uses strong !important rules on the cards
  // and the dropdown, so hiding is set inline (inline !important always wins).
  const hide = (el) => el && el.style.setProperty('display', 'none', 'important');

  function decorateCard(card) {
    // Only the design ticked in the Designs strip shows in the list.
    const strip = studio.querySelector('.zss-v13-designs');
    const anyActive = !!tray.querySelector('.zss-design-card.is-active');
    if (strip && anyActive && !card.classList.contains('is-active')) hide(card);
    else card.style.removeProperty('display');

    const sel = card.querySelector('select[data-zss-design-finish]');
    if (!sel || card.querySelector(':scope > .tlc-fp')) return;
    hide(sel);
    const label = sel.closest('.zss-design-finish, label');
    if (label && label !== card) hide(label);
    const src = (card.querySelector('.zss-design-select img') || {}).src || '';
    const ORDER = ['matte', 'glossy', 'cross', 'leather', 'square', 'shimmer', 'oil'];
    const rank = (id) => { const i = ORDER.indexOf(id); return i < 0 ? 99 : i; };
    const options = Array.from(sel.options)
      .map((o) => ({ value: o.value, name: shortName(o.textContent || o.value), id: finishId(o.value) }))
      .sort((a, b) => rank(a.id) - rank(b.id));
    const current = options.find((o) => o.value === sel.value) || options[0];
    if (!current) return;
    const active = card.classList.contains('is-active');

    const wrap = document.createElement('div');
    wrap.className = 'tlc-fp';
    // the card can be a grid or a flex row depending on the theme layer: give the picker its own full row
    const cs = getComputedStyle(card);
    if (cs.display.indexOf('grid') >= 0) wrap.style.gridColumn = '1 / -1';
    else if (cs.display.indexOf('flex') >= 0 && cs.flexDirection.indexOf('row') === 0) { card.style.flexWrap = 'wrap'; wrap.style.flexBasis = '100%'; }

    if (!active) {
      // other designs: one compact line, tap Change to edit that design
      const row = document.createElement('div');
      row.className = 'tlc-fp-compact';
      const sw = document.createElement('span'); sw.className = 'tlc-fp-sw tlc-fp-sw--mini'; sw.setAttribute('aria-hidden', 'true');
      paintSwatch(sw, src, current.id);
      const txt = document.createElement('span');
      txt.innerHTML = 'Finish: <strong></strong>';
      txt.querySelector('strong').textContent = current.name;
      const change = document.createElement('button');
      change.type = 'button'; change.className = 'tlc-fp-change'; change.textContent = 'Change';
      change.setAttribute('aria-label', 'Change finish for this design');
      change.addEventListener('click', () => { const b = card.querySelector('[data-zss-design-select]'); if (b) b.click(); });
      row.append(sw, txt, change);
      wrap.appendChild(row);
      card.appendChild(wrap);
      return;
    }

    // Option B: "Finish: X" + small swatches on the left, the quantity
    // stepper moved in beside them on the right, one-line description below.
    const row = document.createElement('div');
    row.className = 'tlc-fp-row';
    const left = document.createElement('div');
    left.className = 'tlc-fp-left';
    const head = document.createElement('div');
    head.className = 'tlc-fp-head';
    head.innerHTML = 'Finish: <strong></strong>';
    head.querySelector('strong').textContent = current.name;

    const grid = document.createElement('div');
    grid.className = 'tlc-fp-grid';
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', 'Skin finish');
    options.forEach((o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tlc-fp-tile' + (o.value === current.value ? ' is-on' : '');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', o.value === current.value ? 'true' : 'false');
      b.setAttribute('aria-label', o.name + ' finish');
      b.title = o.name;
      const sw = document.createElement('span'); sw.className = 'tlc-fp-sw'; sw.setAttribute('aria-hidden', 'true');
      paintSwatch(sw, src, o.id);
      b.appendChild(sw);
      b.addEventListener('click', () => {
        if (sel.disabled || sel.value === o.value) return;
        sel.value = o.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
      grid.appendChild(b);
    });
    left.append(head, grid);
    row.appendChild(left);

    // The studio's quantity stepper moves into this row (its buttons keep
    // working: the studio listens on the whole design list). Its look is set
    // inline so it doesn't depend on where the theme expected it to sit.
    const qty = card.querySelector('.zss-design-qty');
    if (qty) {
      const imp = (el, prop, val) => el && el.style.setProperty(prop, val, 'important');
      imp(qty, 'display', 'flex'); imp(qty, 'align-items', 'center'); imp(qty, 'gap', '2px');
      imp(qty, 'border', '1.5px solid #E0E4E0'); imp(qty, 'border-radius', '999px'); imp(qty, 'padding', '0 4px');
      imp(qty, 'margin', '0'); imp(qty, 'flex', '0 0 auto'); imp(qty, 'align-self', 'flex-end'); imp(qty, 'background', '#FFFFFF');
      qty.querySelectorAll(':scope > span').forEach((sp) => imp(sp, 'display', 'none'));
      qty.querySelectorAll('button').forEach((bt) => {
        imp(bt, 'width', '30px'); imp(bt, 'height', '38px'); imp(bt, 'border', '0'); imp(bt, 'background', 'transparent');
        imp(bt, 'padding', '0'); imp(bt, 'font-size', '18px'); imp(bt, 'color', '#2F7A4D'); imp(bt, 'cursor', 'pointer');
      });
      const inp = qty.querySelector('input');
      if (inp) { imp(inp, 'width', '26px'); imp(inp, 'border', '0'); imp(inp, 'background', 'transparent'); imp(inp, 'text-align', 'center'); imp(inp, 'padding', '0'); imp(inp, 'font-size', '15px'); imp(inp, '-moz-appearance', 'textfield'); }
      row.appendChild(qty);
    }

    const info = FINISH_INFO[current.id] || { desc: '' };
    const desc = document.createElement('p');
    desc.className = 'tlc-fp-desc';
    desc.textContent = info.desc;

    // Representation note, shown where the finish is chosen.
    const note = document.createElement('p');
    note.className = 'tlc-fp-disclaimer';
    note.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg><span></span>';
    note.querySelector('span').textContent = DISCLAIMER;

    wrap.append(row, desc, note);
    card.appendChild(wrap);

    // render the other finishes in the background so switching is instant
    options.forEach((o) => swatchFor(src, o.id));
  }

  function decorateReview() {
    if (!review) return;
    review.querySelectorAll('.zss-review-item').forEach((row) => {
      if (row.querySelector('.tlc-fp-note')) return;
      const sel = tray.querySelector('select[data-zss-design-finish="' + row.dataset.zssDesignSelect + '"]');
      const id = sel && finishId(sel.value);
      const info = id && FINISH_INFO[id];
      if (!info) return;
      const host = row.querySelector('span') || row;
      const em = document.createElement('em');
      em.className = 'tlc-fp-note';
      em.textContent = shortName(sel.value) + ': ' + info.note;
      host.appendChild(em);
    });
  }

  const decorateAll = () => { tray.querySelectorAll('.zss-design-card').forEach(decorateCard); decorateReview(); };
  decorateAll();
  if (window.MutationObserver) {
    new MutationObserver(decorateAll).observe(tray, { childList: true });
    if (review) new MutationObserver(decorateReview).observe(review, { childList: true });
  }
}

/* ---------- mobile mini-preview ----------
   On phones the live preview scrolls out of view while the customer is down
   at their design card choosing a finish. A small floating copy of the live
   mockup appears in the top-right corner whenever the main preview is mostly
   off-screen and the design list is on-screen, so each finish change can be
   seen straight away. Tapping it scrolls back up to the full preview. */
function setupPip(studio, live) {
  if (!window.IntersectionObserver || studio.dataset.tlcPip === '1') return;
  studio.dataset.tlcPip = '1';
  const mq = window.matchMedia('(max-width: 749px)');
  const vp = live.closest('[data-viewport]') || live.parentElement;
  const list = studio.querySelector('[data-zss-design-tray-wrap]') || studio.querySelector('[data-zss-design-tray]');
  if (!vp || !list) return;
  const pip = document.createElement('button');
  pip.type = 'button';
  pip.className = 'tlc-pip';
  pip.hidden = true;
  pip.setAttribute('aria-label', 'Live preview. Tap to see it full size');
  const c = document.createElement('canvas'); c.width = c.height = 360;
  const lab = document.createElement('span'); lab.className = 'tlc-pip-label';
  pip.append(c, lab);
  document.body.appendChild(pip);

  let previewOut = false, listIn = false, timer = 0;
  function draw() {
    const cx = c.getContext('2d');
    cx.clearRect(0, 0, 360, 360);
    if (live.width && live.height) cx.drawImage(live, 0, 0, live.width, live.height, 0, 0, 360, 360);
    const id = finishId(activeFinishName(studio));
    lab.textContent = id ? LABELS[id] + ' finish' : 'Preview';
  }
  function update() {
    const show = mq.matches && previewOut && listIn && !!studio.querySelector('.zss-design-card');
    pip.hidden = !show;
    if (show) { draw(); if (!timer) timer = setInterval(draw, 400); }
    else if (timer) { clearInterval(timer); timer = 0; }
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.target === vp) previewOut = e.intersectionRatio < 0.35;
      else listIn = e.isIntersecting;
    });
    update();
  }, { threshold: [0, 0.35, 1] });
  io.observe(vp); io.observe(list);
  pip.addEventListener('click', () => vp.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  if (mq.addEventListener) mq.addEventListener('change', update);
}

/* ---------- ready-made Kindle / Kobo / Boox skin pages ----------
   These pages have no live mockup, just product photos and a Finish
   dropdown. The dropdown is replaced by the same finish swatches (with the
   description and disclaimer), and picking a finish also applies it to the
   product photo that was made on the studio's device template (found by
   locating the template's device frames in each photo, at whatever size and
   position the photo uses), or, for mockups made another way, by finding the
   skin directly: inside the devices, away from the plain background, the
   frame edge and the screen. Photos that don't
   line up are left untouched; the swatches still work. The real dropdown
   stays (hidden) and is what the swatches set, so variants, price and cart
   work exactly as before. */
const MOCKUP = {
  overlay: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/shopify-kindle-mockup-overlay.png?v=1788241795',
  front: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/shopify-kindle-mockup-front-mask.png?v=1788239091',
  back: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/shopify-kindle-mockup-back-mask.png?v=1788239091'
};
function bigImageUrl(img) {
  const u = img.currentSrc || img.src || '';
  try { const x = new URL(u, location.href); x.searchParams.delete('height'); x.searchParams.set('width', '2048'); return x.toString(); }
  catch (e) { return u; }
}
function sampleImage(im, S, T) {
  const c = document.createElement('canvas'); c.width = c.height = S;
  const cx = c.getContext('2d');
  if (T) cx.drawImage(im, T.tx * S, T.ty * S, T.s * S, T.s * S); else cx.drawImage(im, 0, 0, S, S);
  return cx.getImageData(0, 0, S, S).data;
}
const lumAt = (d, i) => .299 * d[i] + .587 * d[i + 1] + .114 * d[i + 2];
/* Template locating. The template layers are sampled once into a grid of
   "frame" cells (opaque dark device frame) and "empty" cells (nothing there,
   so plain background in a photo). A placement T = { s, tx, ty } (template
   scale and offset as fractions of the photo) is scored by checking, for
   every photo cell, that the frame is dark and the empty area is light.
   Only the device frame and the background are used, never the skin, so
   light and dark designs work the same. */
const TPL_R = 256;
function templateGrid(ov, fm, bm) {
  const o = sampleImage(ov, TPL_R), f = sampleImage(fm, TPL_R), b = sampleImage(bm, TPL_R);
  const kind = new Uint8Array(TPL_R * TPL_R);   // 0 = other, 1 = frame, 2 = empty
  let x0 = TPL_R, y0 = TPL_R, x1 = -1, y1 = -1;
  for (let p = 0, i = 0; p < TPL_R * TPL_R; p++, i += 4) {
    if (o[i + 3] > 230 && lumAt(o, i) < 70) kind[p] = 1;
    else if (o[i + 3] < 10 && f[i + 3] < 10 && b[i + 3] < 10) kind[p] = 2;
    if (o[i + 3] > 128 || f[i + 3] > 128 || b[i + 3] > 128) {
      const x = p % TPL_R, y = (p / TPL_R) | 0;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  return { kind, box: x1 < 0 ? null : { x0: x0 / TPL_R, y0: y0 / TPL_R, x1: (x1 + 1) / TPL_R, y1: (y1 + 1) / TPL_R } };
}
function photoLum(photo, S) {
  const d = sampleImage(photo, S), L = new Float32Array(S * S);
  for (let p = 0, i = 0; p < S * S; p++, i += 4) L[p] = lumAt(d, i);
  return L;
}
// Box of everything that isn't plain light background in the photo.
function photoBox(L, S) {
  let x0 = S, y0 = S, x1 = -1, y1 = -1;
  for (let p = 0; p < S * S; p++) if (L[p] < 215) {
    const x = p % S, y = (p / S) | 0;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  return x1 < 0 ? null : { x0: x0 / S, y0: y0 / S, x1: (x1 + 1) / S, y1: (y1 + 1) / S };
}
function gridScore(grid, L, S, T) {
  let frame = 0, frameHit = 0, bg = 0, bgHit = 0;
  for (let y = 0; y < S; y++) {
    const v = ((y + .5) / S - T.ty) / T.s;
    for (let x = 0; x < S; x++) {
      const u = ((x + .5) / S - T.tx) / T.s, l = L[y * S + x];
      let k = 2;   // outside the template = background
      if (u >= 0 && u < 1 && v >= 0 && v < 1) k = grid.kind[((v * TPL_R) | 0) * TPL_R + ((u * TPL_R) | 0)];
      if (k === 1) { frame++; if (l < 100) frameHit++; }
      else if (k === 2) { bg++; if (l > 160) bgHit++; }
    }
  }
  if (frame < S * S * .004 || bg < S * S * .02) return 0;
  return Math.min(frameHit / frame, bgHit / bg);
}
// Finds where (and how big) the studio's device template sits in a product
// photo. Returns { T, score } or null.
async function locateTemplate(photo) {
  const [ov, fm, bm] = await Promise.all([loadImage(MOCKUP.overlay), loadImage(MOCKUP.front), loadImage(MOCKUP.back)]);
  const grid = templateGrid(ov, fm, bm);
  if (!grid.box) return null;
  const S1 = 96, L1 = photoLum(photo, S1), pb = photoBox(L1, S1);
  if (!pb) return null;
  const tb = grid.box, tcx = (tb.x0 + tb.x1) / 2, tcy = (tb.y0 + tb.y1) / 2;
  const pcx = (pb.x0 + pb.x1) / 2, pcy = (pb.y0 + pb.y1) / 2;
  let best = { T: null, score: -1 };
  // wide search: every scale from 55% to 105%, centred on the photo content
  for (let s = .55; s <= 1.05 + 1e-9; s += .025) {
    const cx = pcx - s * tcx, cy = pcy - s * tcy;
    for (let dx = -.05; dx <= .05 + 1e-9; dx += .0125)
      for (let dy = -.05; dy <= .05 + 1e-9; dy += .0125) {
        const T = { s, tx: cx + dx, ty: cy + dy }, sc = gridScore(grid, L1, S1, T);
        if (sc > best.score) best = { T, score: sc };
      }
  }
  // fine search around the best, at a higher sample size
  const S2 = 160, L2 = photoLum(photo, S2);
  best.score = gridScore(grid, L2, S2, best.T);
  [[.025, .005, .0125, .0025], [.006, .0015, .003, .001]].forEach(([sr, ss, tr, ts]) => {
    const c = best.T;
    for (let s = c.s - sr; s <= c.s + sr + 1e-9; s += ss)
      for (let tx = c.tx - tr; tx <= c.tx + tr + 1e-9; tx += ts)
        for (let ty = c.ty - tr; ty <= c.ty + tr + 1e-9; ty += ts) {
          const T = { s, tx, ty }, sc = gridScore(grid, L2, S2, T);
          if (sc > best.score) best = { T, score: sc };
        }
  });
  return best;
}
// Template mask placed into the photo with transform T, cropped to its box.
async function placedMask(url, W, H, T) {
  const key = url + '|' + W + 'x' + H + '|' + T.s.toFixed(4) + ',' + T.tx.toFixed(4) + ',' + T.ty.toFixed(4);
  if (maskCache.has(key)) return maskCache.get(key);
  const im = await loadImage(url);
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const cx = c.getContext('2d');
  cx.drawImage(im, T.tx * W, T.ty * H, T.s * W, T.s * H);
  const d = cx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1, n = 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (d[(y * W + x) * 4 + 3] > 10) { n++; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) { maskCache.set(key, null); return null; }
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  const crop = document.createElement('canvas'); crop.width = w; crop.height = h;
  crop.getContext('2d').drawImage(c, x0, y0, w, h, 0, 0, w, h);
  // fill = share of the box that is skin; the device with the screen cut-out
  // (front of the e-reader) has a much lower fill than the back
  const info = { x0, y0, w, h, crop, work: new Map(), fill: n / (w * h) };
  maskCache.set(key, info);
  return info;
}

/* Skin found directly in a photo (no template): see segmentSkin(). */
const SEG_R = 384;
// Finds the skin in a product photo without a template. rgba: R*R*4 sample.
// Returns { alpha (R*R, 0/255), screen (box in 0..1 or null), frac } or null.
function segmentSkin(rgba, R) {
  const N = R * R, lum = new Float32Array(N), sat = new Float32Array(N);
  for (let p = 0, i = 0; p < N; p++, i += 4) {
    const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
    lum[p] = .299 * r + .587 * g + .114 * b;
    sat[p] = Math.max(r, g, b) - Math.min(r, g, b);
  }
  // 1. background: light, colourless pixels flooded in from the edges
  //    (soft grey shadows count as background; the dark device frame stops it)
  const bg = new Uint8Array(N), q = new Int32Array(N);
  const isBg = (p) => lum[p] > 185 && sat[p] < 22;
  let head = 0, tail = 0;
  const seed = (p) => { if (!bg[p] && isBg(p)) { bg[p] = 1; q[tail++] = p; } };
  for (let x = 0; x < R; x++) { seed(x); seed((R - 1) * R + x); }
  for (let y = 0; y < R; y++) { seed(y * R); seed(y * R + R - 1); }
  while (head < tail) {
    const p = q[head++], x = p % R, y = (p / R) | 0;
    if (x > 0) seed(p - 1); if (x < R - 1) seed(p + 1); if (y > 0) seed(p - R); if (y < R - 1) seed(p + R);
  }
  // 2. distance from the background (to drop the dark frame at the outer edge)
  const dist = new Int32Array(N).fill(-1); head = tail = 0;
  for (let p = 0; p < N; p++) if (bg[p]) { dist[p] = 0; q[tail++] = p; }
  while (head < tail) {
    const p = q[head++], x = p % R, y = (p / R) | 0, d = dist[p] + 1;
    const nb = (n) => { if (dist[n] < 0) { dist[n] = d; q[tail++] = n; } };
    if (x > 0) nb(p - 1); if (x < R - 1) nb(p + 1); if (y > 0) nb(p - R); if (y < R - 1) nb(p + R);
  }
  // 3. the screen: the most rectangular large light area inside a device
  const comp = new Int32Array(N).fill(0); let best = null, id = 0;
  const isLight = (p) => !bg[p] && lum[p] > 170 && sat[p] < 30;
  for (let s = 0; s < N; s++) {
    if (comp[s] || !isLight(s)) continue;
    id++; head = tail = 0; q[tail++] = s; comp[s] = id;
    let x0 = R, y0 = R, x1 = -1, y1 = -1, n = 0;
    while (head < tail) {
      const p = q[head++], x = p % R, y = (p / R) | 0; n++;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      const nb = (m) => { if (!comp[m] && isLight(m)) { comp[m] = id; q[tail++] = m; } };
      if (x > 0) nb(p - 1); if (x < R - 1) nb(p + 1); if (y > 0) nb(p - R); if (y < R - 1) nb(p + R);
    }
    const area = (x1 - x0 + 1) * (y1 - y0 + 1), rect = n / area;
    // a screen sits inside a device: just past each side of it there is
    // still device (frame/skin), not background. A light skin's own area
    // has background just past its sides, so it is never taken as a screen.
    const o = Math.round(R * .04), my = (y0 + y1) >> 1, mx = (x0 + x1) >> 1;
    const inside = (x, y) => x >= 0 && y >= 0 && x < R && y < R && !bg[y * R + x];
    const enclosed = [inside(x0 - o, my), inside(x1 + o, my), inside(mx, y0 - o), inside(mx, y1 + o)].filter(Boolean).length;
    if (n > N * .03 && rect > .6 && enclosed >= 3 && (!best || rect * n > best.rect * best.n)) best = { x0, y0, x1, y1, n, rect };
  }
  const pad = Math.max(1, Math.round(R * .008));   // the screen's own thin bezel
  const sb = best ? { x0: best.x0 - pad, y0: best.y0 - pad, x1: best.x1 + pad, y1: best.y1 + pad } : null;
  // 4. skin = inside a device, away from the outer frame, not the screen
  const edge = Math.max(2, Math.round(R * .016)), alpha = new Uint8Array(N); let skin = 0;
  for (let p = 0; p < N; p++) {
    if (bg[p] || dist[p] <= edge) continue;
    const x = p % R, y = (p / R) | 0;
    if (sb && x >= sb.x0 && x <= sb.x1 && y >= sb.y0 && y <= sb.y1) continue;
    alpha[p] = 255; skin++;
  }
  const frac = skin / N;
  if (frac < .08 || frac > .7) return null;
  return { alpha, frac, screen: sb ? { x0: sb.x0 / R, y0: sb.y0 / R, x1: (sb.x1 + 1) / R, y1: (sb.y1 + 1) / R } : null };
}
// Where to take the swatch close-up: the skin beside (not around) the screen.
function swatchSpot(seg, R) {
  let sx = 0, sy = 0, n = 0;
  for (let p = 0; p < R * R; p++) {
    if (!seg.alpha[p]) continue;
    const x = (p % R) / R, y = ((p / R) | 0) / R;
    if (seg.screen && x <= seg.screen.x1 + .02) continue;
    sx += x; sy += y; n++;
  }
  if (!n) for (let p = 0; p < R * R; p++) if (seg.alpha[p]) { sx += (p % R) / R; sy += ((p / R) | 0) / R; n++; }
  return n ? { x: sx / n, y: sy / n } : { x: .68, y: .55 };
}

// Turns a segmentSkin() result into a full-size mask region for the photo.
function segRegion(seg, W, H) {
  const small = document.createElement('canvas'); small.width = small.height = SEG_R;
  const id = new ImageData(SEG_R, SEG_R);
  for (let p = 0; p < SEG_R * SEG_R; p++) { const i = p * 4; id.data[i] = id.data[i + 1] = id.data[i + 2] = 255; id.data[i + 3] = seg.alpha[p]; }
  small.getContext('2d').putImageData(id, 0, 0);
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const cx = c.getContext('2d'); cx.imageSmoothingQuality = 'high'; cx.drawImage(small, 0, 0, W, H);
  const d = cx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (d[(y * W + x) * 4 + 3] > 10) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return null;
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  const crop = document.createElement('canvas'); crop.width = w; crop.height = h;
  crop.getContext('2d').drawImage(c, x0, y0, w, h, 0, 0, w, h);
  return { x0, y0, w, h, crop, work: new Map() };
}

/* Known product-photo layouts (the main photo of the ready-made Kindle/Kobo
   and Boox listings). Each was measured from the store's own listings: the
   mask is where the skin differs between designs, the ref holds the pixels
   that are the same in every listing (background, device frame, screen), so a
   photo is recognised when those pixels match. Exact position, no search. */
const LAYOUTS = [
  { name: 'kindle-main',
    mask: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/tlc-finish-kindle-main-mask.png?v=1792452107',
    ref: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/tlc-finish-kindle-main-ref.png?v=1792452107',
    pieceFrac: .457, pieceMm: 125, swatch: { x: .69, y: .55, side: .22 } },
  { name: 'boox-main',
    mask: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/tlc-finish-boox-main-mask.png?v=1792452107',
    ref: 'https://cdn.shopify.com/s/files/1/0793/4882/6407/files/tlc-finish-boox-main-ref.png?v=1792452107',
    pieceFrac: .55, pieceMm: 137, swatch: { x: .265, y: .32, side: .18 } }
];
const LAYOUT_R = 128;
// Splits a layout mask into one region per device (like the studio's
// front/back pieces), so each device gets its own light and texture scale.
function splitMask(info) {
  const { w, h, crop } = info;
  const d = crop.getContext('2d').getImageData(0, 0, w, h).data;
  const N = w * h, lab = new Int32Array(N), q = new Int32Array(N), comps = [];
  for (let s = 0; s < N; s++) {
    if (lab[s] || d[s * 4 + 3] < 128) continue;
    const id = comps.length + 1; let head = 0, tail = 0, n = 0, x0 = w, y0 = h, x1 = -1, y1 = -1;
    q[tail++] = s; lab[s] = id;
    while (head < tail) {
      const p = q[head++], x = p % w, y = (p / w) | 0; n++;
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      const nb = (m) => { if (!lab[m] && d[m * 4 + 3] >= 128) { lab[m] = id; q[tail++] = m; } };
      if (x > 0) nb(p - 1); if (x < w - 1) nb(p + 1); if (y > 0) nb(p - w); if (y < h - 1) nb(p + w);
    }
    comps.push({ id, n, x0, y0, x1, y1 });
  }
  const big = comps.filter((c) => c.n > N * .02);
  if (big.length < 2) return [info];
  return big.map((c) => {
    // grow the box a little so the soft (anti-aliased) edge is kept
    const bx0 = Math.max(0, c.x0 - 3), by0 = Math.max(0, c.y0 - 3), bx1 = Math.min(w - 1, c.x1 + 3), by1 = Math.min(h - 1, c.y1 + 3);
    const cw = bx1 - bx0 + 1, ch = by1 - by0 + 1;
    const id = new ImageData(cw, ch);
    for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) {
      const p = (y + by0) * w + (x + bx0), a = d[p * 4 + 3];
      if (!a) continue;
      let own = lab[p] === c.id;
      if (!own && !lab[p]) {   // soft edge pixel: keep it if it touches this device
        for (let k = -2; k <= 2 && !own; k++) for (let j = -2; j <= 2 && !own; j++) {
          const xx = x + bx0 + j, yy = y + by0 + k;
          if (xx >= 0 && yy >= 0 && xx < w && yy < h && lab[yy * w + xx] === c.id) own = true;
        }
      }
      if (own) { const i = (y * cw + x) * 4; id.data[i] = id.data[i + 1] = id.data[i + 2] = 255; id.data[i + 3] = a; }
    }
    const cc = document.createElement('canvas'); cc.width = cw; cc.height = ch;
    cc.getContext('2d').putImageData(id, 0, 0);
    return { x0: info.x0 + bx0, y0: info.y0 + by0, w: cw, h: ch, crop: cc, work: new Map() };
  });
}
async function matchLayout(photo, L) {
  const ref = await loadImage(L.ref);
  const r = sampleImage(ref, LAYOUT_R);
  // listing photos can be transparent PNGs: judge them on white, as shown
  const c = document.createElement('canvas'); c.width = c.height = LAYOUT_R;
  const cx = c.getContext('2d'); cx.fillStyle = '#fff'; cx.fillRect(0, 0, LAYOUT_R, LAYOUT_R);
  cx.drawImage(photo, 0, 0, LAYOUT_R, LAYOUT_R);
  const p = cx.getImageData(0, 0, LAYOUT_R, LAYOUT_R).data;
  let n = 0, hit = 0;
  for (let i = 0; i < r.length; i += 4) {
    if (r[i + 3] < 200) continue;
    n++;
    if (Math.abs(r[i] - p[i]) <= 24 && Math.abs(r[i + 1] - p[i + 1]) <= 24 && Math.abs(r[i + 2] - p[i + 2]) <= 24) hit++;
  }
  return n ? hit / n : 0;
}
async function locateLayout(photo) {
  let best = null;
  for (const L of LAYOUTS) {
    try { const score = await matchLayout(photo, L); if (!best || score > best.score) best = { L, score }; }
    catch (e) { console.warn('[TLC finish] layout ' + L.name + ' could not be checked:', e && e.message ? e.message : e); }
  }
  return best;
}

function setupRegular() {
  if (document.querySelector('[data-zss-studio]')) return;
  const main = document.querySelector('[id^="MainProduct-"]');
  if (!main || main.dataset.tlcRegFinish === '1') return;
  const fbox = Array.from(main.querySelectorAll('.product-form__input--dropdown'))
    .find((b) => /finish/i.test((b.querySelector('.form__label, label') || {}).textContent || ''));
  const sel = fbox && fbox.querySelector('select');
  if (!sel) return;
  const ORDER = ['matte', 'glossy', 'cross', 'leather', 'square', 'shimmer', 'oil'];
  const rank = (id) => { const i = ORDER.indexOf(id); return i < 0 ? 99 : i; };
  const options = Array.from(sel.options).filter((o) => o.value)
    .map((o) => ({ value: o.value, name: shortName(o.textContent || o.value), id: finishId(o.value) }))
    .filter((o) => o.id).sort((a, b) => rank(a.id) - rank(b.id));
  if (!options.length) return;
  main.dataset.tlcRegFinish = '1';

  /* swatches UI */
  const selectWrap = sel.closest('.select') || sel;
  selectWrap.style.setProperty('display', 'none', 'important');
  const wrap = document.createElement('div');
  wrap.className = 'tlc-rfp';
  const head = document.createElement('div'); head.className = 'tlc-rfp-head';
  const grid = document.createElement('div'); grid.className = 'tlc-rfp-grid';
  grid.setAttribute('role', 'radiogroup'); grid.setAttribute('aria-label', 'Skin finish');
  const desc = document.createElement('p'); desc.className = 'tlc-fp-desc';
  const note = document.createElement('p'); note.className = 'tlc-fp-disclaimer';
  note.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg><span></span>';
  note.querySelector('span').textContent = DISCLAIMER;
  const tiles = options.map((o) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'tlc-rfp-tile';
    b.setAttribute('role', 'radio'); b.setAttribute('aria-label', o.name + ' finish');
    const sw = document.createElement('span'); sw.className = 'tlc-fp-sw'; sw.setAttribute('aria-hidden', 'true');
    const nm = document.createElement('span'); nm.className = 'tlc-rfp-name'; nm.textContent = o.name;
    b.append(sw, nm);
    b.addEventListener('click', () => {
      if (sel.value === o.value) return;
      sel.value = o.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
    });
    grid.appendChild(b);
    return { o, b, sw };
  });
  wrap.append(head, grid, desc, note);
  fbox.appendChild(wrap);

  function sync() {
    const cur = options.find((o) => o.value === sel.value);
    head.innerHTML = cur ? 'Finish: <strong></strong>' : 'Choose a finish';
    if (cur) head.querySelector('strong').textContent = cur.name;
    tiles.forEach((t) => {
      const on = !!cur && t.o.value === cur.value;
      t.b.classList.toggle('is-on', on);
      t.b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const info = cur && FINISH_INFO[cur.id];
    desc.textContent = info ? info.desc : 'Tap a finish to see how it looks on this design.';
    renderPhoto(cur ? cur.id : null);
  }
  sel.addEventListener('change', () => setTimeout(sync, 0));

  /* the product photo that matches the studio template */
  let target = null, gen = 0, stopShineFn = null;
  function paintSwatches(src) { tiles.forEach((t) => paintSwatch(t.sw, src, t.o.id)); }

  async function findPhoto() {
    const imgs = Array.from(main.querySelectorAll('.product__media-list .product__media-item img')).slice(0, 5);
    let firstPhoto = null;
    if (!imgs.length) console.info('[TLC finish] no product photos found on the page');
    // 1. the store's standard listing photos (known layouts, exact position)
    for (let i = 0; i < imgs.length; i++) {
      let photo;
      try { photo = await loadImage(bigImageUrl(imgs[i])); } catch (e) { continue; }
      if (!firstPhoto) firstPhoto = photo;
      const hit = await locateLayout(photo);
      if (hit) console.info('[TLC finish] photo ' + (i + 1) + ' layout ' + hit.L.name + ' match ' + Math.round(hit.score * 100) + '%');
      if (hit && hit.score >= 0.85) { console.info('[TLC finish] finish will be shown on photo ' + (i + 1)); return { img: imgs[i], photo, aligned: true, layout: hit.L }; }
    }
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i], url = bigImageUrl(img);
      let photo;
      try { photo = await loadImage(url); }
      catch (e) { console.warn('[TLC finish] photo ' + (i + 1) + ' could not be loaded for the preview (blocked or missing):', url); continue; }
      if (!firstPhoto) firstPhoto = photo;
      try {
        const hit = await locateTemplate(photo);
        const score = hit ? hit.score : 0;
        console.info('[TLC finish] photo ' + (i + 1) + ' template match ' + Math.round(score * 100) + '%' +
          (hit ? ' (scale ' + hit.T.s.toFixed(3) + ', x ' + hit.T.tx.toFixed(3) + ', y ' + hit.T.ty.toFixed(3) + ')' : ''));
        if (hit && score >= 0.85) { console.info('[TLC finish] finish will be shown on photo ' + (i + 1)); return { img, photo, aligned: true, T: hit.T }; }
      } catch (e) { console.warn('[TLC finish] photo ' + (i + 1) + ' could not be checked:', e && e.message ? e.message : e); }
    }
    // no template match: find the skin in the photo itself
    for (let i = 0; i < imgs.length; i++) {
      let photo;
      try { photo = await loadImage(bigImageUrl(imgs[i])); } catch (e) { continue; }
      try {
        const seg = segmentSkin(sampleImage(photo, SEG_R), SEG_R);
        if (seg) {
          console.info('[TLC finish] photo ' + (i + 1) + ': skin found directly (' + Math.round(seg.frac * 100) + '% of the photo' + (seg.screen ? ', screen excluded' : '') + '); finish will be shown on it');
          return { img: imgs[i], photo, aligned: true, seg };
        }
        console.info('[TLC finish] photo ' + (i + 1) + ': no skin area found');
      } catch (e) { console.warn('[TLC finish] photo ' + (i + 1) + ' could not be checked:', e && e.message ? e.message : e); }
    }
    console.info('[TLC finish] no photo could be used; finish shown on the swatches only');
    return firstPhoto ? { img: imgs[0], photo: firstPhoto, aligned: false } : null;
  }

  async function prepare() {
    const found = await findPhoto();
    if (!found) return;
    const { img, photo } = found;
    const W = photo.naturalWidth || photo.width, H = photo.naturalHeight || photo.height;
    // swatch close-ups: from the skin itself when the photo lines up, else the photo centre
    let swSrc = '';
    try {
      const c = document.createElement('canvas');
      if (found.layout) {
        const sp = found.layout.swatch, side = Math.round(Math.min(W, H) * sp.side);
        c.width = c.height = side;
        const sx = Math.min(W - side, Math.max(0, sp.x * W - side / 2)), sy = Math.min(H - side, Math.max(0, sp.y * H - side / 2));
        c.getContext('2d').drawImage(photo, sx, sy, side, side, 0, 0, side, side);
      } else if (found.seg) {
        // the skin beside the screen (the back of the e-reader)
        const spot = swatchSpot(found.seg, SEG_R), side = Math.round(Math.min(W, H) * 0.22);
        c.width = c.height = side;
        const sx = Math.min(W - side, Math.max(0, spot.x * W - side / 2)), sy = Math.min(H - side, Math.max(0, spot.y * H - side / 2));
        c.getContext('2d').drawImage(photo, sx, sy, side, side, 0, 0, side, side);
      } else if (found.aligned) {
        // the back of the e-reader (no screen): the mask that fills most of its box
        const ms = await Promise.all([placedMask(MOCKUP.front, W, H, found.T), placedMask(MOCKUP.back, W, H, found.T)]);
        const back = ms.filter(Boolean).sort((p, q) => q.fill - p.fill)[0];
        const side = Math.round(Math.min(back.w, back.h) * 0.6);
        c.width = c.height = side;
        c.getContext('2d').drawImage(photo, back.x0 + (back.w - side) / 2, back.y0 + (back.h - side) / 2, side, side, 0, 0, side, side);
      } else {
        // no template match: the right-hand side of the photo (usually the back of the device)
        const side = Math.round(Math.min(W, H) * 0.3);
        c.width = c.height = side;
        c.getContext('2d').drawImage(photo, W * 0.68 - side / 2, H * 0.55 - side / 2, side, side, 0, 0, side, side);
      }
      swSrc = c.toDataURL('image/jpeg', 0.9) + '#closeup';
    } catch (e) { swSrc = ''; }
    if (swSrc) paintSwatches(swSrc);
    if (!found.aligned) return;
    const media = img.parentElement;
    const cv = document.createElement('canvas');
    cv.className = 'tlc-rfp-photo';
    cv.width = W; cv.height = H;
    cv.hidden = true;
    cv.style.objectFit = getComputedStyle(img).objectFit || 'cover';
    const badge = document.createElement('div');
    badge.className = 'tlc-finish-badge'; badge.hidden = true;
    media.append(cv, badge);
    let seg = null, ppmOverride = 0;
    if (found.layout) {
      try { seg = await pieceMask(found.layout.mask, W, H); } catch (e) { seg = null; }
      if (!seg) { console.warn('[TLC finish] layout mask could not be loaded'); return; }
      ppmOverride = W * found.layout.pieceFrac / found.layout.pieceMm;
    } else if (found.seg) seg = segRegion(found.seg, W, H);
    let parts = null;
    if (found.layout) { try { parts = splitMask(seg); } catch (e) { parts = null; } }
    target = { img, photo, W, H, cv, badge, li: img.closest('li'), T: found.T, seg, parts, ppm: ppmOverride, photoTune: !!found.layout };
    if (sel.value) sync();
  }

  async function renderPhoto(id) {
    const my = ++gen;
    if (stopShineFn) { stopShineFn(); stopShineFn = null; }
    if (!target) return;
    const { photo, W, H, cv, badge } = target;
    if (!id) { cv.hidden = true; badge.hidden = true; return; }
    badge.hidden = false; badge.textContent = LABELS[id] + ' finish · rendering…'; badge.classList.add('is-busy');
    try {
      const snap = document.createElement('canvas'); snap.width = W; snap.height = H;
      snap.getContext('2d').drawImage(photo, 0, 0);
      let regions, ppm;
      if (target.seg) {
        regions = (target.parts || [target.seg]).map((m) => ({ x0: m.x0, y0: m.y0, w: m.w, h: m.h, mask: m }));
        ppm = target.ppm || W * .4 / PIECE_WIDTH_MM;   // known layout, else each device is roughly 40% of the photo width
      } else {
        const [front, back] = await Promise.all([placedMask(MOCKUP.front, W, H, target.T), placedMask(MOCKUP.back, W, H, target.T)]);
        regions = [back, front].filter(Boolean).map((m) => ({ x0: m.x0, y0: m.y0, w: m.w, h: m.h, mask: m }));
        ppm = (front ? front.w : W * .6) / PIECE_WIDTH_MM;
      }
      if (my !== gen) return;
      const shown = cv.offsetWidth || target.img.offsetWidth;
      const screenPxPerMm = shown ? ppm * (shown * (window.devicePixelRatio || 1)) / W : ppm * .35;
      // same preset and real-size texture as the custom-order live preview
      const preset = previewPreset(id, screenPxPerMm);
      const parts = await Promise.all(regions.map((r) => renderRegion(snap, r, preset, ppm)));
      if (my !== gen) return;
      const comp = document.createElement('canvas'); comp.width = W; comp.height = H;
      const cc = comp.getContext('2d');
      cc.drawImage(snap, 0, 0);
      regions.forEach((r, i) => cc.drawImage(parts[i], r.x0, r.y0));
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(comp, 0, 0);
      cv.hidden = false;
      badge.textContent = LABELS[id] + ' finish · preview'; badge.classList.remove('is-busy');
      if (id === 'glossy') {
        stopShineFn = runShine(ctx, (im, ...a) => ctx.drawImage(im, ...a), comp, shineMaskFor(regions, W, H), W, H, () => my === gen);
      }
      // on phones the gallery is a slider: bring the finished photo into view
      const ul = target.li && target.li.parentElement;
      if (ul && ul.scrollWidth > ul.clientWidth + 4 && target.li.previousElementSibling) {
        ul.scrollTo({ left: target.li.offsetLeft - ul.offsetLeft, behavior: 'smooth' });
      }
    } catch (err) {
      cv.hidden = true; badge.hidden = true;
      console.warn('[TLC finish] product photo preview disabled:', err);
    }
  }

  sync();
  prepare();
}

/* ---------- studio hook ---------- */
function hook(studio) {
  setupPicker(studio);
  if (studio.dataset.tlcFinish === '1') return;
  const section = studio.closest('section') || document;
  const live = section.querySelector('[data-canvas]');
  if (!live) return;
  studio.dataset.tlcFinish = '1';
  const targets = [];

  // Live mockup: the two skin areas come from the front/back masks.
  targets.push(makeTarget(studio, live, {
    name: 'live',
    shine: true,
    col: section.querySelector('[data-live-col]'),
    regions: async (W, H) => {
      const [front, back] = await Promise.all([
        pieceMask(studio.dataset.frontMaskUrl, W, H),
        pieceMask(studio.dataset.backMaskUrl, W, H)
      ]);
      return [back, front].filter(Boolean).map((m) => ({ x0: m.x0, y0: m.y0, w: m.w, h: m.h, mask: m }));
    },
    // Studio convention: front piece treated as 125 mm wide
    ppm: (W, H, regions) => { const f = regions[regions.length - 1]; return (f ? f.w : W * .6) / PIECE_WIDTH_MM; }
  }));

  // Print layout (cutfile): the pieces are on a transparent background and
  // are printed at real size (300 dpi), with the cut lines drawn on top.
  const print = section.querySelector('[data-print-canvas]');
  if (print) {
    targets.push(makeTarget(studio, print, {
      name: 'print',
      col: section.querySelector('[data-print-col]'),
      keepOverlay: true,
      regions: async (W, H) => [{ x0: 0, y0: 0, w: W, h: H, mask: null }],
      ppm: () => 300 / 25.4
    }));
  }

  setupPip(studio, live);

  // Finish dropdown on each design card.
  studio.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('[data-zss-design-finish]')) targets.forEach((t) => t.schedule(40));
  });
}

function injectStyles() {
  if (document.getElementById('tlc-finish-style-v10')) return;
  const st = document.createElement('style');
  st.id = 'tlc-finish-style-v10';
  st.textContent =
    '.tlc-finish-badge{position:absolute;top:10px;left:10px;z-index:3;pointer-events:none;' +
    'font-size:12px;line-height:1.2;font-weight:500;letter-spacing:.02em;color:#2c2620;background:rgba(255,255,255,.88);' +
    'border:1px solid rgba(77,112,80,.35);border-radius:999px;padding:6px 11px;box-shadow:0 2px 8px rgba(0,0,0,.08);' +
    'transition:opacity .2s}' +
    '.tlc-finish-badge.is-busy{opacity:.65}' +
    '.tlc-finish-badge[hidden]{display:none!important}' +
    '.tlc-fp-hidden{display:none!important}' +
    '.tlc-fp{width:100%;box-sizing:border-box;display:flex;flex-direction:column;gap:6px;margin-top:6px}' +
    '.tlc-fp-row{display:flex;align-items:flex-end;justify-content:space-between;gap:8px;flex-wrap:nowrap}' +
    '.tlc-fp-left{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:6px}' +
    '.tlc-fp-head{font-size:13px}' +
    '.tlc-fp-head strong{font-weight:500}' +
    '.tlc-fp-grid{display:flex;gap:5px;flex-wrap:nowrap;min-width:0}' +
    '.zwapskin-studio .zss-design-card .tlc-fp .tlc-fp-grid .tlc-fp-tile{flex:1 1 0!important;width:auto!important;height:auto!important;max-width:56px!important;min-width:0!important}' +
    '.zwapskin-studio .zss-design-card .tlc-fp .tlc-fp-row .zss-design-qty{flex:0 0 auto!important}' +
    '.tlc-fp-tile{appearance:none;-webkit-appearance:none;margin:0;flex:0 0 auto;width:56px!important;height:56px!important;max-width:56px!important;min-width:0!important;aspect-ratio:1/1;padding:2px;box-sizing:border-box;' +
    'border:1.5px solid #E0E4E0;background:#FFFFFF;border-radius:10px;cursor:pointer;display:flex;align-items:center;justify-content:center}' +
    '.tlc-fp-tile.is-on{border:2px solid #2F7A4D}' +
    '.tlc-fp-tile:focus-visible{outline:2px solid #2F7A4D;outline-offset:2px}' +
    '.tlc-fp-sw{display:block;width:100%;height:100%;border-radius:7px;background:#F1F4F1 center/cover no-repeat}' +
    '.tlc-fp-sw--mini{width:28px;height:28px;flex-shrink:0;border-radius:6px}' +
    '.tlc-fp-desc{margin:0;font-size:12.5px;line-height:1.4;color:#5F665F}' +
    '.tlc-fp-disclaimer{margin:0;display:flex;align-items:flex-start;gap:5px;font-size:11.5px;line-height:1.4;color:#7A827C}' +
    '.tlc-fp-disclaimer svg{flex-shrink:0;margin-top:2px}' +
    '[data-zss-studio] select[data-zss-design-finish]{display:none!important}' +
    '.tlc-fp .zss-design-qty input::-webkit-outer-spin-button,.tlc-fp .zss-design-qty input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}' +
    '.tlc-fp-compact{display:flex;align-items:center;gap:8px;font-size:13px}' +
    '.tlc-fp-change{margin-left:auto;background:none;border:0;padding:0 4px;min-height:44px;font:inherit;font-size:13px;font-weight:600;color:#2F7A4D;text-decoration:underline;cursor:pointer}' +
    '.tlc-fp-note{display:block;font-style:normal;font-size:12px;line-height:1.3;color:#4F5A52;margin-top:2px}' +
    '.tlc-pip{position:fixed;z-index:60;top:calc(env(safe-area-inset-top,0px) + 10px);right:10px;width:42vw;max-width:190px;padding:0;margin:0;' +
    'border:1px solid #DDE3DD;border-radius:14px;background:#FFFFFF;box-shadow:0 8px 24px rgba(0,0,0,.18);overflow:hidden;cursor:pointer;display:block}' +
    '.tlc-pip[hidden]{display:none}' +
    '.tlc-pip canvas{display:block;width:100%;height:auto;aspect-ratio:1/1;background:#F7F8F6}' +
    '.tlc-pip-label{position:absolute;left:6px;bottom:6px;font-size:11px;line-height:1.2;padding:3px 7px;border-radius:999px;background:rgba(255,255,255,.92);color:#2C3A30;border:1px solid #DDE3DD}' +
    '@media(max-width:749px){.zwapskin-studio .zss-design-card .tlc-fp .tlc-fp-grid .tlc-fp-tile{max-width:48px!important}}' +
    '@media(min-width:750px){.tlc-pip{display:none!important}}' +
    '.tlc-rfp{display:flex;flex-direction:column;gap:8px;margin-top:6px}' +
    '.tlc-rfp-head{font-size:1.4rem}' +
    '.tlc-rfp-head strong{font-weight:600}' +
    '.tlc-rfp-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px;max-width:440px}' +
    '.tlc-rfp-tile{appearance:none;-webkit-appearance:none;margin:0;padding:4px 3px 6px;border:1.5px solid #E0D8C8;border-radius:12px;background:#FFFFFF;cursor:pointer;' +
    'display:flex;flex-direction:column;align-items:center;gap:5px;font:inherit;font-size:1.15rem;line-height:1.15;color:inherit;min-width:0;text-align:center}' +
    '.tlc-rfp-tile .tlc-fp-sw{width:100%;height:auto;aspect-ratio:1/1;border-radius:8px}' +
    '.tlc-rfp-tile.is-on{border:2px solid #4D7050;background:#EEF4EC;font-weight:600}' +
    '.tlc-rfp-tile:focus-visible{outline:2px solid #4D7050;outline-offset:2px}' +
    '.tlc-rfp-name{display:block;max-width:100%;overflow-wrap:anywhere}' +
    '.tlc-rfp .tlc-fp-desc{font-size:1.3rem}' +
    '.tlc-rfp .tlc-fp-disclaimer{font-size:1.15rem}' +
    '.tlc-rfp-photo{position:absolute;inset:0;width:100%;height:100%;z-index:1;pointer-events:none}' +
    '.tlc-rfp-photo[hidden]{display:none!important}' +
    '.product__media-item .tlc-finish-badge{z-index:2;top:10px!important;left:10px!important;right:auto!important;bottom:auto!important;width:auto!important;height:auto!important;max-width:calc(100% - 20px)}';
  document.head.appendChild(st);
}

function boot() {
  injectStyles();
  document.querySelectorAll('[data-zss-studio]').forEach(hook);
  setupRegular();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
setTimeout(boot, 800);
document.addEventListener('shopify:section:load', () => setTimeout(boot, 80));
})();
